'use client';

import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { useSheetStackParams } from '@hooks/use-sheet-stack-params';
import { menuHref } from '@utils/route-utils';
import type { SearchHit } from '../../search/types';

/**
 * Resolves a `SearchHit.destination` to an actual navigation action,
 * matching `RecipeSearchCommand`'s existing routes exactly: recipes open
 * the mobile sheet stack on mobile and route to the beverage detail page
 * on desktop (recipes carry no food/beverage kind yet); menus open their
 * kind's detail route; ingredients always open the ingredient sheet. Centralized here so the desktop dialog and
 * mobile drawer perform identical navigation.
 */
export function useKnowledgeSearchNavigation(
  organizationId: string,
  isMobile: boolean
) {
  const router = useRouter();
  const { openRecipe, openIngredient } = useSheetStackParams();

  return useCallback(
    (hit: SearchHit) => {
      switch (hit.destination.kind) {
        case 'recipe':
          if (isMobile) {
            openRecipe(hit.destination.recipeId);
          } else {
            router.push(
              `/${organizationId}/bev/recipes/${hit.destination.recipeId}`
            );
          }
          return;
        case 'ingredient':
          openIngredient(hit.destination.ingredientId);
          return;
        case 'menu':
          router.push(
            menuHref(organizationId, {
              id: hit.destination.menuId,
              kind: hit.destination.menuKind
            })
          );
          return;
        case 'reference':
        case 'handbook':
          router.push(
            `/${organizationId}/library/reference/${hit.destination.articleId}`
          );
          return;
        case 'person':
          router.push(`/${organizationId}/employees`);
          return;
        case 'channel':
          router.push(
            `/${organizationId}/messages/${hit.destination.channelId}`
          );
          return;
      }
    },
    [router, organizationId, isMobile, openRecipe, openIngredient]
  );
}
